/* ComplianceDigestCard — in-app rendering of today's compliance drift
 * digest (iter 98.25). Shown on the doctor dashboard between the
 * ComplianceStrip and PatientNudges so the first thing a doctor sees on
 * login is "who drifted overnight".
 *
 * The endpoint we hit (`/doctor/compliance-digest/today`) is itself
 * idempotent + side-effect-y: the FIRST call of the day computes the
 * drift, persists the snapshot, and fires the email. Every subsequent
 * call returns the cached summary. This means rendering this card on
 * dashboard mount also triggers the daily email — the pull-based pattern
 * the rest of the codebase uses (see `_auto_no_show_sweep` for prior art).
 */
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
    Bell, Warning, ArrowRight, CheckCircle, EnvelopeSimple, Gear,
    ArrowsClockwise,
} from "@phosphor-icons/react";


const BUCKET_TONES = {
    "needs-attention": { color: "#E11D48", label: "Needs attention", chip: "bg-rose-50 text-rose-700 border-rose-200" },
    "at-risk":         { color: "#F59E0B", label: "At risk",         chip: "bg-amber-50 text-amber-700 border-amber-200" },
    "on-track":        { color: "#007C9D", label: "On track",        chip: "bg-primary/10 text-primary border-primary/30" },
    "new":             { color: "#94A3B8", label: "New patient",     chip: "bg-secondary text-muted-foreground border-border" },
};


export default function ComplianceDigestCard() {
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);

    useEffect(() => {
        api.get("/doctor/compliance-digest/today")
            .then((r) => setData(r.data))
            .catch(() => setData(null))
            .finally(() => setLoading(false));
    }, []);

    const items = data?.items || [];
    const driftCount = data?.drift_count || 0;
    const emailSent = data?.email_sent;
    const skipped = data?.skipped_reason;

    const handleRefire = async () => {
        setRefreshing(true);
        try {
            const r = await api.post("/doctor/compliance-digest/run");
            setData(r.data);
        } finally {
            setRefreshing(false);
        }
    };

    if (loading) {
        return (
            <div className="bg-card border border-border rounded-xl px-4 py-3 text-sm text-muted-foreground inline-flex items-center gap-2">
                <Bell size={14} weight="duotone" className="text-primary animate-pulse" /> Checking overnight drift…
            </div>
        );
    }

    if (driftCount === 0) {
        return (
            <div className="bg-card border border-border rounded-xl px-4 py-3 flex items-center gap-3" data-testid="compliance-digest-card">
                <div className="w-9 h-9 rounded-full bg-primary/10 grid place-items-center shrink-0">
                    <CheckCircle size={16} weight="duotone" className="text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold">No drift overnight</div>
                    <div className="text-xs text-muted-foreground">Every active patient stayed in their bucket. Nice.</div>
                </div>
                <Link
                    to="/doctor/cases?view=compliance"
                    className="text-[11px] text-muted-foreground hover:text-primary inline-flex items-center gap-1"
                    data-testid="compliance-digest-prefs-link"
                >
                    <Gear size={11} /> Preferences
                </Link>
            </div>
        );
    }

    return (
        <div className="bg-card border border-rose-200/60 rounded-xl overflow-hidden shadow-sm" data-testid="compliance-digest-card">
            <div className="px-4 py-3 bg-gradient-to-r from-rose-50 to-rose-50/0 border-b border-rose-200/60 flex items-center gap-3 flex-wrap">
                <div className="w-9 h-9 rounded-full bg-rose-100 grid place-items-center shrink-0">
                    <Warning size={16} weight="duotone" className="text-rose-600" />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold">
                        {driftCount} patient{driftCount !== 1 ? "s" : ""} drifted overnight
                    </div>
                    <div className="text-[11px] text-muted-foreground inline-flex items-center gap-1.5">
                        {emailSent ? (
                            <><EnvelopeSimple size={11} /> Digest email sent</>
                        ) : skipped === "already_run_today" ? (
                            <>Digest already sent today</>
                        ) : skipped ? (
                            <span className="text-amber-700">Email skipped: {skipped}</span>
                        ) : null}
                    </div>
                </div>
                <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleRefire}
                    disabled={refreshing}
                    className="text-[11px] h-7 rounded-full"
                    data-testid="compliance-digest-resend-btn"
                    title="Re-fire today's digest email"
                >
                    <ArrowsClockwise size={11} className={`mr-1 ${refreshing ? "animate-spin" : ""}`} /> Resend
                </Button>
                <Link
                    to="/doctor/cases?view=compliance"
                    className="inline-flex items-center gap-1 text-[11px] font-semibold text-primary hover:text-primary/80"
                    data-testid="compliance-digest-open-compliance"
                >
                    Open compliance <ArrowRight size={11} />
                </Link>
            </div>
            <ul className="divide-y divide-border/60 max-h-72 overflow-y-auto">
                {items.slice(0, 6).map((item) => {
                    const toTone = BUCKET_TONES[item.to_bucket] || BUCKET_TONES["needs-attention"];
                    const fromTone = BUCKET_TONES[item.from_bucket] || BUCKET_TONES["new"];
                    return (
                        <li
                            key={item.case_id}
                            onClick={() => navigate(`/doctor/cases/${item.case_id}`)}
                            className="px-4 py-2.5 hover:bg-secondary/40 transition-colors cursor-pointer group"
                            data-testid={`compliance-digest-row-${item.case_id}`}
                        >
                            <div className="flex items-center gap-3">
                                <div className="flex-1 min-w-0">
                                    <div className="text-[13px] font-medium truncate group-hover:text-primary transition">{item.patient_name || "Patient"}</div>
                                    <div className="text-[11px] text-muted-foreground flex items-center gap-1.5 flex-wrap">
                                        <span className="font-mono">{item.case_code || "—"}</span>
                                        <span>·</span>
                                        <span>Aligner {item.stage || 0}</span>
                                        {item.wear_7d_avg != null && (
                                            <>
                                                <span>·</span>
                                                <span className="tabular-nums">{item.wear_7d_avg}h avg</span>
                                            </>
                                        )}
                                        {item.days_since_last_log != null && item.days_since_last_log >= 2 && (
                                            <>
                                                <span>·</span>
                                                <span className="text-amber-700">silent {item.days_since_last_log}d</span>
                                            </>
                                        )}
                                    </div>
                                </div>
                                <div className="flex items-center gap-1.5 shrink-0">
                                    <span className={`inline-flex items-center text-[10px] font-medium px-1.5 py-0.5 rounded-full border ${fromTone.chip}`}>
                                        {fromTone.label}
                                    </span>
                                    <ArrowRight size={10} className="text-muted-foreground/60" />
                                    <span className={`inline-flex items-center text-[10px] font-bold px-1.5 py-0.5 rounded-full border ${toTone.chip}`}>
                                        {toTone.label}
                                    </span>
                                </div>
                            </div>
                        </li>
                    );
                })}
            </ul>
            {items.length > 6 && (
                <div className="px-4 py-2 bg-secondary/30 border-t border-border text-[11px] text-center">
                    <Link to="/doctor/cases?view=compliance" className="text-primary font-semibold">
                        +{items.length - 6} more in the compliance view →
                    </Link>
                </div>
            )}
        </div>
    );
}
